/**
 * Permission Matrix Utilities
 * Transforms API data into matrix structure for the PermissionMatrix component
 */

import { Permission, PermissionsByModule } from '../../types/rbac.types';

// --- Types ---

export interface MatrixGroup {
  groupName: string;
  permissions: Permission[];
  actionMap: Record<string, Permission>; // action → Permission
}

export interface PermissionMatrixData {
  groups: MatrixGroup[];
  actionColumns: string[]; // canonical actions present in at least 1 group
  totalPermissions: number;
}

// --- Constants ---

/** Canonical order: CRUD first, then special actions */
export const ACTION_ORDER: string[] = [
  'read',
  'create',
  'update',
  'delete',
  'manage',
  'approve',
  'export',
  'import',
];

/** PT-BR labels for each action */
export const ACTION_LABELS: Record<string, string> = {
  // CRUD padrão
  read: 'Ver',
  view: 'Ver',
  create: 'Criar',
  update: 'Editar',
  delete: 'Excluir',
  // Gestão
  manage: 'Gerenciar',
  approve: 'Aprovar',
  export: 'Exportar',
  import: 'Importar',
  configure: 'Configurar',
  backup: 'Backup',
  search: 'Buscar',
  // Agendamentos
  block: 'Bloquear',
  statistics: 'Estatisticas',
  book_outside_slot: 'Fora do Horário',
  approve_own: 'Aprovar Proprios',
  approve_clinic: 'Aprovar Clinica',
  reject: 'Rejeitar',
  view_pending: 'Ver Pendentes',
  checkin: 'Check-in',
  complete: 'Finalizar',
  replicate: 'Replicar',
  generate: 'Gerar',
  // Prontuário / ASO
  'generate-aso': 'Gerar ASO',
  'generate-guide': 'Gerar Guia',
  'generate-prontuario': 'Gerar Prontuario',
  'generate-documents': 'Gerar Documentos',
  // eSocial / CIPAA
  submit: 'Transmitir',
  cancel: 'Cancelar',
  'manage-candidates': 'Gerenciar Candidatos',
  'manage-votes': 'Gerenciar Votacao',
};

/** MUI color per action */
export const ACTION_COLORS: Record<string, 'info' | 'success' | 'warning' | 'error' | 'primary' | 'secondary'> = {
  read: 'info',
  view: 'info',
  create: 'success',
  update: 'warning',
  delete: 'error',
  manage: 'primary',
  approve: 'secondary',
  approve_own: 'secondary',
  approve_clinic: 'secondary',
  export: 'primary',
  import: 'primary',
  configure: 'warning',
  block: 'error',
  reject: 'error',
  cancel: 'error',
  complete: 'success',
  checkin: 'success',
  generate: 'info',
  'generate-aso': 'info',
  'generate-guide': 'info',
  'generate-prontuario': 'info',
  'generate-documents': 'info',
  replicate: 'warning',
  submit: 'success',
  backup: 'warning',
  statistics: 'info',
  view_pending: 'info',
  book_outside_slot: 'warning',
  'manage-candidates': 'primary',
  'manage-votes': 'primary',
};

/** Standard CRUD actions */
const STANDARD_ACTIONS = new Set(['read', 'create', 'update', 'delete']);

/** Module display names fallback (used when permissionGroup is not available) */
const MODULE_DISPLAY_NAMES: Record<string, string> = {
  users: 'Usuarios',
  rbac: 'Controle de Acesso',
  companies: 'Empresas',
  employees: 'Funcionarios',
  departments: 'Setores',
  occupations: 'Cargos',
  professionals: 'Profissionais',
  providers: 'Prestadores',
  schedules: 'Agendamentos',
  appointments: 'Atendimentos',
  exams: 'Exames',
  reports: 'Relatorios',
  settings: 'Configuracoes',
  system: 'Sistema',
};

// --- Helper functions ---

export function getActionLabel(action: string): string {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  // Fallback: replace separators with spaces and capitalize each word
  return action
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getActionColor(action: string): 'info' | 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  return ACTION_COLORS[action] || 'primary';
}

export function isStandardAction(action: string): boolean {
  return STANDARD_ACTIONS.has(action);
}

export function getModuleDisplayName(module: string): string {
  return MODULE_DISPLAY_NAMES[module] || module;
}

// --- Main builder ---

/**
 * Transforms PermissionsByModule (from API) into a matrix-friendly structure.
 * Groups are sorted by displayOrder (if available) or alphabetically.
 * Action columns only include actions present in at least 1 group.
 */
export function buildPermissionMatrix(data: PermissionsByModule): PermissionMatrixData {
  const allActionsUsed = new Set<string>();
  let totalPermissions = 0;

  const groups: MatrixGroup[] = Object.entries(data).map(([groupName, permissions]) => {
    const actionMap: Record<string, Permission> = {};

    for (const perm of permissions) {
      actionMap[perm.action] = perm;
      allActionsUsed.add(perm.action);
      totalPermissions++;
    }

    return {
      groupName,
      permissions,
      actionMap,
    };
  });

  // Sort groups: by first permission's displayOrder, then alphabetically
  groups.sort((a, b) => {
    const orderA = a.permissions[0]?.displayOrder ?? 999;
    const orderB = b.permissions[0]?.displayOrder ?? 999;
    if (orderA !== orderB) return orderA - orderB;
    return a.groupName.localeCompare(b.groupName, 'pt-BR');
  });

  // Build action columns: canonical order first, then any extras alphabetically
  const actionColumns: string[] = [];
  for (const action of ACTION_ORDER) {
    if (allActionsUsed.has(action)) {
      actionColumns.push(action);
    }
  }
  // Add any actions not in ACTION_ORDER
  for (const action of allActionsUsed) {
    if (!ACTION_ORDER.includes(action)) {
      actionColumns.push(action);
    }
  }

  return { groups, actionColumns, totalPermissions };
}

/**
 * Separate standard (CRUD) action columns from "special" ones.
 * Standard columns get their own table columns; specials go into the "Outros" column.
 */
export function splitActionColumns(actionColumns: string[]): {
  standardColumns: string[];
  specialColumns: string[];
} {
  const standardColumns = actionColumns.filter((a) => isStandardAction(a));
  const specialColumns = actionColumns.filter((a) => !isStandardAction(a));
  return { standardColumns, specialColumns };
}
